import { clearToken, isTokenExpired } from '@/lib/auth';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3000/api';

export function apiUrl(path: string): string {
  return `${BASE_URL}${path}`;
}

/** Preserves the HTTP status alongside the message — a plain `Error` (thrown here previously)
 * discarded it, so callers had no way to special-case e.g. a 409 optimistic-concurrency
 * conflict from any other failure. `message`-only callers (`catch (e) { setError(e.message) }`)
 * keep working unchanged since this still IS an `Error`. */
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * The body, or a thrown error saying it could not be had.
 *
 * `res.json().catch(() => null)` was the whole of this, and the `null` it produced was then cast
 * to `T` and handed to the caller as if the request had succeeded. A page that asked for an
 * experience got `null` and crashed on `exp.tasks` several frames later, reported as "something
 * went wrong" with a stack pointing at the component rather than at the request -- which is how a
 * transport problem spent a long time looking like a task that had gone missing.
 *
 * The three cases are genuinely different and only one of them is an error:
 *
 *  - The body never arrived intact: `res.text()` itself rejects when the stream is cut off
 *    mid-response (a navigation, a dev-server restart, a dropped connection). The status line
 *    already said 200, so nothing else will ever notice. THROW.
 *  - There is no body at all: a 204, or any endpoint that answers with nothing. `null` is the
 *    honest value and several callers rely on it.
 *  - There is a body and it is not JSON: a proxy's HTML error page served with a 200, or a
 *    truncated payload. Also THROW -- silently returning `null` is what hid it.
 */
async function readBody(res: Response): Promise<unknown> {
  let text: string;
  try {
    text = await res.text();
  } catch {
    throw new ApiError('The response was cut off before it finished', res.status);
  }
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ApiError('The response was not valid JSON', res.status);
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  /*
   * Read BEFORE the status checks, but let a failed read past them for a non-OK response: an
   * error status with an unreadable body is still an error, and reporting it as "not valid JSON"
   * would bury the 401 or 500 the caller actually needs to branch on.
   */
  let data: { error?: { message?: string }; message?: string } | null = null;
  try {
    data = (await readBody(res)) as { error?: { message?: string }; message?: string } | null;
  } catch (e) {
    if (res.ok) throw e;
    // Non-OK with an unreadable body: fall through with `data` null so the status-based paths
    // below report the 401/409/500 the caller branches on, not the body that came with it.
  }

  /*
   * Only a SPENT token is thrown away.
   *
   * This used to treat every 401 as "your session expired" and wipe the token, so one 401 from one
   * endpoint — or a request that raced the backend coming back up — discarded a token with days
   * left on it and bounced the teacher to /login. A 401 means the server refused THIS request; it
   * does not say the credential is finished, and only the credential itself can say that.
   *
   * So: expired (or unreadable) → clear and send them to sign in, which is the honest reading. Still
   * live → leave it alone and let the 401 surface as an ordinary error the caller can report. A
   * token that the server rejects for some other reason then produces a visible failure instead of
   * a silent logout, which is the right way round: never discard a working credential on a guess.
   */
  if (res.status === 401 && isTokenExpired()) {
    clearToken();
    window.location.href = `${import.meta.env.BASE_URL}login`;
    throw new Error('Session expired');
  }

  if (!res.ok) throw new ApiError(data?.error?.message ?? data?.message ?? 'Something went wrong', res.status);
  return data as T;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('token');
  const res = await fetch(apiUrl(path), {
    ...options,
    /*
     * The trusted-device cookie rides on this.
     *
     * It is HttpOnly, so script cannot attach it by hand — the browser only sends it when the
     * request opts in, and cross-origin `fetch` does not by default. Set here rather than on the
     * two auth calls that need it: this is also what lets a future endpoint use a cookie without
     * anyone having to remember why some requests carry credentials and others do not. CORS is
     * already configured with `credentials: true`.
     */
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });
  return handleResponse<T>(res);
}

async function requestForm<T>(path: string, formData: FormData): Promise<T> {
  const token = localStorage.getItem('token');
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    credentials: 'include',
    // No Content-Type here — the browser sets the multipart boundary itself.
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: formData,
  });
  return handleResponse<T>(res);
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  postForm: <T>(path: string, formData: FormData) => requestForm<T>(path, formData),
};
